import { useCallback } from 'react'
import { useNavigate } from 'react-router'
import { useSWR } from 'modules/network/hooks/useSwr'
import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'
import { useBlockExplorerName } from 'modules/blockChain/hooks/useBlockExplorerName'
import { useBlockExplorerOpener } from 'modules/blockChain/hooks/useBlockExplorerOpener'

import { Text } from 'shared/ui/common/Text'
import { Title } from 'shared/ui/common/Title'
import { Button } from 'shared/ui/controls/Button'
import { NFCSGraph } from '../NFCSGraph'
import { NFCSMintImprove } from '../NFCSMintImprove'
import { RoundCTAButton } from 'shared/ui/controls/RoundCTAButton'
import { ProgressBar } from 'shared/ui/common/ProgressBar'

import { ContractNFCS } from 'modules/contracts/contracts'
import { getTokenEvent } from 'modules/nfcs/utils/getTokenEvent'
import { isBadNFCSScore, isFraudNFCSScore } from 'modules/nfcs/utils/NFCSScores'
import { trimMiddleString } from 'shared/utils/trimMiddleString'
import { openWindow } from 'shared/utils/openWindow'
import * as links from 'modules/router/links'

import { ReactComponent as CyberconnectIcon } from 'assets/cyberconnect.svg'
import { ReactComponent as RelationLabsIcon } from 'assets/relationlabs.svg'
import { ReactComponent as LensIcon } from 'assets/lens.svg'

import s from './NFCSMintFinish.module.scss'

const SCORES_DISTRIBUTION: Record<number, number> = {
  1: 1,
  2: 2,
  3: 4,
  4: 12,
  5: 17,
  6: 25,
  7: 21,
}

const CYBERCONNECT_URL = 'https://app.cyberconnect.me/plaza'
const RELATION_LABS_URL = 'https://relationlabs.ai/?utm_source=rocifi#/bazaar'
const LENS_URL = 'https://www.lens.xyz/'

type Props = {
  nfcs: number
  tokenId: number | undefined | null
  walletAddress: string | undefined | null
  isSelf?: boolean
  isLoading?: boolean
  onClose?: () => void
  onClickAdd?: () => void
}

export function NFCSMintFinish({
  nfcs,
  tokenId,
  walletAddress,
  isSelf,
  isLoading,
  onClose,
  onClickAdd,
}: Props) {
  const { chainId } = useWeb3()
  const navigate = useNavigate()
  const blockExplorerName = useBlockExplorerName()

  const goToBorrow = useCallback(() => {
    navigate(links.borrow)
    onClose?.()
  }, [navigate, onClose])

  const { data: primaryAddress, isLoading: isLoadingPrimary } =
    ContractNFCS.useSwrRpc(Boolean(walletAddress) && 'getPrimaryAddress', [
      walletAddress!,
    ])

  const { data: nfcsBundle, isLoading: isLoadingBundle } =
    ContractNFCS.useSwrRpc(typeof tokenId === 'number' && 'getBundle', [
      tokenId!,
    ])

  const { data: tokenEvent, isLoading: isLoadingTokenEvent } = useSWR(
    Boolean(typeof tokenId === 'number' && walletAddress && primaryAddress) &&
      `nfcs-token-event-${walletAddress}-${tokenId}-${chainId}-${primaryAddress}`,
    async () => {
      if (!walletAddress || typeof tokenId !== 'number' || !primaryAddress) {
        return null
      }
      const contract = ContractNFCS.connectRpc({ chainId })
      const event = await getTokenEvent(contract, primaryAddress, tokenId)
      return event
    },
  )

  const handleClickOpenOnBlockExplorer = useBlockExplorerOpener(
    tokenEvent?.raw.transactionHash,
    'tx',
  )

  const handleAddToCyberconnect = () => openWindow(CYBERCONNECT_URL)
  const handleAddToRelationLabs = () => openWindow(RELATION_LABS_URL)
  const handleAddToLens = () => openWindow(LENS_URL)

  if (isLoading || isLoadingPrimary || isLoadingBundle || isLoadingTokenEvent) {
    return <ProgressBar isCentered />
  }

  if (nfcs === -1) {
    return (
      <Title className={s.title} isCentered>
        This address
        <br /> does not yet possess NFCS
      </Title>
    )
  }

  if (isFraudNFCSScore(nfcs)) {
    if (isSelf) {
      return (
        <>
          <Title className={s.title} isCentered>
            You cannot borrow <br /> from RociFi at the moment
          </Title>
          <Text isUppercased isCentered>
            Please improve your score and come back
          </Text>
        </>
      )
    }
    return (
      <Title className={s.title} isCentered>
        This address <br />
        has fraud NFCS status
      </Title>
    )
  }

  if (isBadNFCSScore(nfcs)) {
    if (isSelf) {
      return (
        <>
          <Title className={s.title} isCentered>
            You cannot borrow <br /> from RociFi at the moment
          </Title>
          <Text isUppercased isCentered>
            Please repay your loan first
          </Text>
        </>
      )
    }
    return (
      <Title className={s.title} isCentered>
        This address <br />
        has unpaid loan
      </Title>
    )
  }

  return (
    <>
      <Text
        size={16}
        weight={600}
        isUppercased
        isCentered
        className={s.undertitle}
      >
        {isSelf ? (
          <>Your Credit Score is generated</>
        ) : (
          <>
            Address {trimMiddleString(String(walletAddress), 4)} has possessed
            NFCS
          </>
        )}
      </Text>

      <div className={s.title}>
        <Text tag="span" size={44} weight={600} isUppercased>
          {isSelf && 'Your '}Credit Score:{' '}
        </Text>
        <Text
          tag="span"
          size={44}
          weight={700}
          color="branded"
          className={s.nfcsTitle}
        >
          {nfcs}
        </Text>
      </div>

      {SCORES_DISTRIBUTION[nfcs] && (
        <Text
          size={12}
          weight={500}
          isUppercased
          isCentered
          className={s.nfcsDistribution}
        >
          <Text tag="span" color="secondary">
            Congratulations!{' '}
          </Text>
          <Text tag="span">
            Only {SCORES_DISTRIBUTION[nfcs]}% of DeFi users{' '}
          </Text>
          <Text tag="span" color="secondary">
            get this score
          </Text>
        </Text>
      )}

      {onClickAdd && (
        <div className={s.addAddressWrap}>
          <RoundCTAButton size={333} borderOffset={56} onClick={onClickAdd}>
            <Text size={12} weight={500} isCentered isUppercased>
              Add more <br />
              Addresses to the <br />
              NFCS
            </Text>
          </RoundCTAButton>
        </div>
      )}

      <NFCSGraph nfcs={nfcs} className={s.graph} />

      <div className={s.bundleInfo}>
        <Text size={16} weight={600} isUppercased isCentered>
          NFCS ID: {tokenId}
        </Text>

        {nfcsBundle && primaryAddress && (
          <>
            <br />
            <Text
              color="secondary"
              size={12}
              weight={500}
              isUppercased
              isCentered
            >
              Addresses that have been added to the bundle:
            </Text>
            {nfcsBundle.map(address => (
              <Text
                key={address}
                size={12}
                weight={600}
                isUppercased
                isCentered
              >
                {address} {address === primaryAddress && '(primary)'}
              </Text>
            ))}
          </>
        )}

        <br />

        <div className={s.blockExplorerLink}>
          <Text
            color="secondary"
            tag="span"
            size={12}
            weight={600}
            isUppercased
          >
            See on&nbsp;
          </Text>
          <Text
            color="greenapple"
            tag="span"
            size={12}
            weight={600}
            isUppercased
            onClick={handleClickOpenOnBlockExplorer}
            className={s.explorerLink}
          >
            {blockExplorerName}
          </Text>
        </div>
      </div>

      {isSelf && (
        <div className={s.did}>
          <Text isUppercased size={16} weight={600}>
            Add your NFCS to web3 social graphs
          </Text>
          <div className={s.didButtons}>
            <Button
              fashion="glass"
              size={147}
              className={s.didButton}
              onClick={handleAddToCyberconnect}
            >
              <span>Add to cyberconnect</span>
              <CyberconnectIcon />
            </Button>
            <Button
              fashion="glass"
              size={147}
              className={s.didButton}
              onClick={handleAddToRelationLabs}
            >
              <span>
                Add to the <br />
                relationlabs
              </span>
              <RelationLabsIcon />
            </Button>
            <Button
              fashion="glass"
              size={147}
              className={s.didButton}
              onClick={handleAddToLens}
              testId="nfcs-mint-finish-add-relation-button"
            >
              <span>
                Add to <br />
                Lens
              </span>
              <LensIcon />
            </Button>
          </div>
        </div>
      )}

      {isSelf && (
        <>
          <Text size={44} weight={600} isUppercased className={s.improveTitle}>
            Actions you can take to&nbsp;improve the score
          </Text>

          <NFCSMintImprove className={s.improveBlock} />

          <RoundCTAButton
            size={400}
            borderOffset={50}
            className={s.startBorrow}
            onClick={goToBorrow}
          >
            <Text
              size={24}
              weight={600}
              isCentered
              isUppercased
              className={s.label}
            >
              Start <br />
              borrow <br />
              flow
            </Text>
          </RoundCTAButton>
        </>
      )}
    </>
  )
}
